import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  adminPoolApi,
  READING_TYPES,
  LISTENING_TYPES,
  CEFR_LEVELS,
  TYPE_LABEL_KEY,
  type PoolText,
  type PoolAudio,
  type PoolTask,
} from '@/api/ieltsMock';
import { PageHeader } from '@/components/ui/PageHeader';
import { AudioPlayer } from '@/components/ui/AudioPlayer';
import { Card, CardTitle } from '@/components/ui/Card';
import { Pill } from '@/components/ui/Pill';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { humanizeError } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { ActionSheet } from '@/components/mobile/ActionSheet';
import { MChips } from '@/components/mobile/MobileKit';
import { MoreButton } from '@/components/mobile/MoreButton';
import { useIsMobile } from '@/hooks/useIsMobile';
import { PoolKeyReview } from './PoolKeyReview';

type Tab = 'reading' | 'listening' | 'writing' | 'speaking' | 'review';
const TABS: { key: Tab; label?: string; labelKey?: string }[] = [
  { key: 'reading', label: 'Reading' },
  { key: 'listening', label: 'Listening' },
  { key: 'writing', label: 'Writing' },
  { key: 'speaking', label: 'Speaking' },
  { key: 'review', labelKey: 'adminPool.reviewTab' },
];

const levelOpts = CEFR_LEVELS.map((l) => ({ value: l, label: l }));

/* one text/audio row with expandable tasks (answer keys visible to admin) */
function ContentRow({
  title,
  subtitle,
  audioUrl,
  active = true,
  onToggle,
  loadTasks,
}: {
  title: string;
  subtitle: string;
  audioUrl?: string;
  active?: boolean;
  onToggle?: (next: boolean) => void;
  loadTasks: () => Promise<PoolTask[]>;
}) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [sheet, setSheet] = useState(false);
  const tasks = useQuery({ queryKey: ['pool-tasks', title, subtitle], queryFn: loadTasks, enabled: open });
  return (
    <Card variant="subtle" className={cn('space-y-2', !active && 'opacity-60')}>
      <div className="flex items-center justify-between gap-3">
        <button className="flex flex-1 items-center gap-3 text-left" onClick={() => setOpen((o) => !o)}>
          <div>
            <div className="font-medium">
              {title} {!active && <span className="text-xs text-on-surface-variant">{t('adminPool.hidden')}</span>}
            </div>
            <div className="text-xs text-on-surface-variant">{subtitle}</div>
          </div>
        </button>
        {onToggle && isMobile && (
          // Телефон (§5.15): «Скрыть / Вернуть» — листом «⋯», строка остаётся названию.
          <>
            <MoreButton onClick={() => setSheet(true)} />
            <ActionSheet
              open={sheet}
              onClose={() => setSheet(false)}
              title={title}
              actions={[
                {
                  label: t(active ? 'adminPool.hide' : 'adminPool.restore'),
                  // visibility_off в шрифт не входит (сторож iconSubset) — «скрыть» значком архива.
                  icon: active ? 'archive' : 'visibility',
                  onSelect: () => onToggle(!active),
                },
              ]}
            />
          </>
        )}
        {onToggle && !isMobile && (
          <Button variant="ghost" size="sm" onClick={() => onToggle(!active)}>
            {t(active ? 'adminPool.hide' : 'adminPool.restore')}
          </Button>
        )}
        <button onClick={() => setOpen((o) => !o)}>
          <span className="material-symbols-outlined text-on-surface-variant">{open ? 'expand_less' : 'expand_more'}</span>
        </button>
      </div>
      {audioUrl && <AudioPlayer src={audioUrl} preload="none" />}
      {open && (
        <div className="space-y-3 border-t border-outline pt-3">
          {tasks.isLoading && <div className="text-sm text-on-surface-variant">{t('adminPool.loadingTasks')}</div>}
          {tasks.data?.map((task) => (
            <div key={task.id} className="rounded-lg bg-surface-container p-3 text-sm">
              <div className="mb-1 flex items-center gap-2">
                <Pill variant="primary">{t(TYPE_LABEL_KEY[task.type] ?? task.type)}</Pill>
                <span className="text-xs text-on-surface-variant">{task.group_label}</span>
              </div>
              {task.options && task.options.length > 0 && (
                <div className="mb-1 text-xs text-on-surface-variant">{t('adminPool.options', { list: task.options.join(' · ') })}</div>
              )}
              <ol className="space-y-0.5">
                {task.questions.map((q) => (
                  <li key={q.id} className="flex justify-between gap-3">
                    <span>
                      {q.ord}. {q.prompt}
                    </span>
                    {/* На телефоне ключ переносится: длинный ответ в одну строку выталкивал карточку за край. */}
                    <span className="whitespace-nowrap font-semibold text-primary max-md:whitespace-normal max-md:text-right">
                      → {q.answer_key}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          ))}
          {tasks.data && tasks.data.length === 0 && <div className="text-sm text-on-surface-variant">{t('adminPool.noTasks')}</div>}
        </div>
      )}
    </Card>
  );
}

export function AdminIeltsPoolPage() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('reading');
  const skillLabel = TABS.find((tb) => tb.key === tab)?.label;
  const [level, setLevel] = useState('B2');
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [note, setNote] = useState('');

  const texts = useQuery({ queryKey: ['pool', 'texts'], queryFn: () => adminPoolApi.listTexts(), enabled: tab === 'reading' });
  const audios = useQuery({ queryKey: ['pool', 'audios'], queryFn: () => adminPoolApi.listAudios(), enabled: tab === 'listening' });
  const writing = useQuery({ queryKey: ['pool', 'writing'], queryFn: () => adminPoolApi.listWriting(), enabled: tab === 'writing' });
  const speaking = useQuery({ queryKey: ['pool', 'speaking'], queryFn: () => adminPoolApi.listSpeaking(), enabled: tab === 'speaking' });

  const gen = useMutation({
    mutationFn: async () => {
      const types = selectedTypes.length ? selectedTypes : undefined;
      if (tab === 'reading') return adminPoolApi.generateReading({ level, types });
      if (tab === 'listening') return adminPoolApi.generateListening({ level, types });
      if (tab === 'writing') return adminPoolApi.generateWriting({ level, kind: 'task2' });
      return adminPoolApi.generateSpeaking({ level });
    },
    onSuccess: () => {
      if (tab === 'reading' || tab === 'listening') {
        setNote(t('adminPool.genStarted'));
      } else {
        setNote(t('adminPool.genDone'));
        qc.invalidateQueries({ queryKey: ['pool', tab] });
      }
    },
    onError: (e) => setNote(humanizeError(e)),
  });

  const toggleText = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => adminPoolApi.setTextActive(id, active),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pool', 'texts'] }),
  });
  const toggleAudio = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => adminPoolApi.setAudioActive(id, active),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pool', 'audios'] }),
  });

  const typeChoices = tab === 'reading' ? READING_TYPES : tab === 'listening' ? LISTENING_TYPES : [];

  return (
    <>
      <PageHeader title={t('adminPool.title')} subtitle={t('adminPool.subtitle')} />

      {isMobile ? (
        // Телефон (§5.15): пять вкладок в строку без переноса уходили за край на 390 — фишками с прокруткой.
        <div className="mb-3" data-pool-tabs-phone>
          <MChips
            chips={TABS.map((tb) => ({ key: tb.key, label: tb.label ?? t(tb.labelKey!) }))}
            active={tab}
            onChange={(k) => {
              setTab(k);
              setSelectedTypes([]);
              setNote('');
            }}
          />
        </div>
      ) : (
      <div className="mb-4 flex gap-2">
        {TABS.map((tb) => (
          <button
            key={tb.key}
            onClick={() => {
              setTab(tb.key);
              setSelectedTypes([]);
              setNote('');
            }}
            className={cn(
              'rounded-full px-4 py-1.5 text-sm font-medium transition',
              tab === tb.key ? 'bg-primary text-on-primary' : 'bg-surface-container text-on-surface-variant',
            )}
          >
            {tb.label ?? t(tb.labelKey!)}
          </button>
        ))}
      </div>
      )}

      {/* generate panel */}
      <Card className="mb-6 space-y-4">
        <CardTitle>{t('adminPool.generate')}</CardTitle>
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-28">
            <Select label={t('cueSets.level')} options={levelOpts} value={level} onChange={(e) => setLevel(e.target.value)} />
          </div>
          <Button onClick={() => gen.mutate()} loading={gen.isPending}>
            {/* У вкладки «Ключи на проверку» нет названия навыка — тогда просто «Сгенерировать». */}
            {skillLabel ? t('adminPool.generateSkill', { skill: skillLabel }) : t('adminPool.generate')}
          </Button>
        </div>

        {typeChoices.length > 0 && (
          <div className="space-y-2">
            <div className="text-xs text-on-surface-variant">
              {t('adminPool.typesHint')}
            </div>
            <div className="flex flex-wrap gap-2">
              {typeChoices.map((tp) => {
                const on = selectedTypes.includes(tp);
                return (
                  <button
                    key={tp}
                    onClick={() =>
                      setSelectedTypes((s) => (on ? s.filter((x) => x !== tp) : [...s, tp]))
                    }
                    className={cn(
                      'rounded-full border px-3 py-1 text-xs transition',
                      on ? 'border-primary bg-primary/10 text-primary' : 'border-outline text-on-surface-variant',
                    )}
                  >
                    {/* Через t(): прежде фишки показывали сам ключ «taskTypes.mc_single» (с 24.08, кадр стенда 13.10). */}
                    {t(TYPE_LABEL_KEY[tp] ?? tp)}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {note && <div className="text-sm text-primary">{note}</div>}
      </Card>

      {/* lists */}
      {tab === 'reading' && (
        <div className="space-y-3">
          <ListHeader onRefresh={() => texts.refetch()} count={texts.data?.length} />
          {texts.data?.map((tx: PoolText) => (
            <ContentRow
              key={tx.id}
              title={tx.title}
              subtitle={t('adminPool.textMeta', { level: tx.cefr_level, count: tx.word_count, source: tx.source })}
              active={tx.active}
              onToggle={(next) => toggleText.mutate({ id: tx.id, active: next })}
              loadTasks={() => adminPoolApi.textTasks(tx.id)}
            />
          ))}
        </div>
      )}
      {tab === 'listening' && (
        <div className="space-y-3">
          <ListHeader onRefresh={() => audios.refetch()} count={audios.data?.length} />
          {audios.data?.map((a: PoolAudio) => (
            <ContentRow
              key={a.id}
              title={a.title}
              subtitle={`${a.cefr_level} · ${a.duration_sec}с · ${a.source}`}
              audioUrl={a.audio_url}
              active={a.active}
              onToggle={(next) => toggleAudio.mutate({ id: a.id, active: next })}
              loadTasks={() => adminPoolApi.audioTasks(a.id)}
            />
          ))}
        </div>
      )}
      {tab === 'writing' && (
        <div className="space-y-3">
          <ListHeader onRefresh={() => writing.refetch()} count={writing.data?.length} />
          {writing.data?.map((w) => (
            <Card key={w.id} variant="subtle" className="space-y-1">
              <Pill variant="primary">{w.kind}</Pill>
              <p className="whitespace-pre-line text-sm">{w.prompt}</p>
            </Card>
          ))}
        </div>
      )}
      {tab === 'speaking' && (
        <div className="space-y-3">
          <ListHeader onRefresh={() => speaking.refetch()} count={speaking.data?.length} />
          {speaking.data?.map((s) => (
            <Card key={s.id} variant="subtle" className="space-y-1">
              <Pill variant="primary">Part {s.part}</Pill>
              {s.topic && <div className="text-sm font-medium">{s.topic}</div>}
              {s.cue_text && <p className="whitespace-pre-line text-sm">{s.cue_text}</p>}
              {s.questions?.map((qq, i) => (
                <div key={i} className="text-sm text-on-surface-variant">
                  • {qq}
                </div>
              ))}
            </Card>
          ))}
        </div>
      )}

      {tab === 'review' && <PoolKeyReview />}
    </>
  );
}

function ListHeader({ onRefresh, count }: { onRefresh: () => void; count?: number }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-on-surface-variant">{t('adminPool.total', { n: count ?? '…' })}</span>
      <Button variant="ghost" size="sm" onClick={onRefresh}>
        {t('adminPool.refresh')}
      </Button>
    </div>
  );
}
